import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { BookOpen, GraduationCap, PlayCircle, FileText, Bell, Users, ArrowRight, CheckCircle, Clock } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading, login, register, googleLogin } = useAuth();
  const [isAuthMode, setIsAuthMode] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  const [featuredCourses, setFeaturedCourses] = useState([]);

  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'superadmin') {
        navigate('/superadmin');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
    fetchFeaturedCourses();
  }, [user, loading, navigate]);

  const fetchFeaturedCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/public/featured`);
      setFeaturedCourses(response.data);
    } catch (error) {
      console.error("Failed to fetch courses");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      toast.success("Welcome back!");
      if (userData.role === 'superadmin') {
        navigate('/superadmin');
      } else if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await register(formData.email, formData.password, formData.name);
      toast.success("Account created successfully!");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const features = [
    { icon: PlayCircle, title: "Lecture Recordings", description: "Access video lectures anytime, anywhere" },
    { icon: FileText, title: "Learning Materials", description: "PDFs, documents, and resources at your fingertips" },
    { icon: Bell, title: "Assignment Notifications", description: "Never miss a deadline with timely alerts" },
    { icon: BookOpen, title: "Course Content", description: "Structured learning paths for your success" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-30 w-full bg-white/80 backdrop-blur-xl border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-violet-700" />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>LearnHub</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              data-testid="login-btn"
              onClick={() => setIsAuthMode(true)}
              className="rounded-full px-6"
            >
              Sign In
            </Button>
            <Link to="/register">
              <Button 
                data-testid="get-started-btn"
                className="bg-violet-700 text-white hover:bg-violet-800 rounded-full px-6"
              >
                Register
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-stone-900" style={{ fontFamily: 'Outfit' }}>
                Your Learning Journey Starts Here
              </h1>
              <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-xl">
                Access lecture recordings, learning materials, and course content. Stay on top of assignments with smart notifications.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    data-testid="hero-get-started-btn"
                    className="bg-violet-700 text-white hover:bg-violet-800 rounded-full px-8 py-6 text-lg font-medium transition-all active:scale-95"
                  >
                    Start Learning <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button 
                  variant="outline"
                  onClick={() => setIsAuthMode(true)}
                  className="border-2 border-stone-200 text-stone-700 hover:border-violet-700 hover:text-violet-700 rounded-full px-8 py-6 text-lg"
                >
                  Sign In
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in animation-delay-200">
              <img 
                src="https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwbGFwdG9wJTIwbGlicmFyeXxlbnwwfHx8fDE3NzAzNzAzNjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Student studying"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Everything You Need
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              A complete learning platform designed for modern students
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-violet-700 transition-colors">
                    <feature.icon className="h-6 w-6 text-violet-700 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                    {feature.title}
                  </h3>
                  <p className="text-stone-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      {featuredCourses.length > 0 && (
        <section className="py-16 md:py-24 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                  Featured Courses
                </h2>
                <p className="text-lg text-stone-600 max-w-2xl">
                  Start your learning journey with our most popular courses
                </p>
              </div>
              <Link to="/explore-courses">
                <Button variant="outline" className="rounded-full">
                  View All Courses <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCourses.slice(0, 6).map((course) => (
                <Link 
                  key={course.course_id} 
                  to={`/course/${course.course_id}`}
                  data-testid={`landing-course-${course.course_id}`}
                >
                  <Card className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-xl hover:border-violet-200 transition-all duration-300 h-full group">
                    {/* Thumbnail */}
                    <div className="aspect-video bg-gradient-to-br from-violet-500 to-purple-600 relative overflow-hidden">
                      {course.thumbnail ? (
                        <img 
                          src={course.thumbnail.startsWith('http') ? course.thumbnail : `${API.replace('/api', '')}${course.thumbnail}`}
                          alt={course.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="h-16 w-16 text-white/50" />
                        </div>
                      )}
                      <Badge className="absolute top-4 left-4 bg-white/90 text-violet-700 backdrop-blur-sm">
                        {course.category}
                      </Badge>
                    </div>
                    
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-stone-900 mb-2 line-clamp-2 group-hover:text-violet-700 transition-colors" style={{ fontFamily: 'Outfit' }}>
                        {course.title}
                      </h3>
                      
                      <p className="text-stone-500 text-sm mb-4 line-clamp-2">
                        {course.short_description || course.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-stone-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{course.duration || '8 weeks'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          <span>{course.enrolled_count || 0} enrolled</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                        <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                          {course.price > 0 ? `£${course.price}` : 'Free'}
                        </p>
                        <Button variant="ghost" className="text-violet-700 hover:text-violet-800 group-hover:translate-x-1 transition-transform">
                          View <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Auth Modal */}
      {isAuthMode && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsAuthMode(false)}
        >
          <Card 
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-4">
                <GraduationCap className="h-12 w-12 text-violet-700" />
              </div>
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Welcome to LearnHub</CardTitle>
              <CardDescription>Sign in to access your courses</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="h-12"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="h-12"
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="login-submit-btn"
                      className="w-full bg-violet-700 hover:bg-violet-800 h-12 rounded-xl"
                      disabled={authLoading}
                    >
                      {authLoading ? "Signing in..." : "Sign In"}
                    </Button>
                    <div className="text-center">
                      <Link to="/forgot-password" className="text-sm text-violet-700 hover:underline">
                        Forgot your password?
                      </Link>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="h-12"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="h-12"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="h-12"
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="register-submit-btn"
                      className="w-full bg-violet-700 hover:bg-violet-800 h-12 rounded-xl"
                      disabled={authLoading}
                    >
                      {authLoading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-stone-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-stone-500">Or continue with</span>
                </div>
              </div>

              <Button 
                type="button"
                data-testid="google-login-btn"
                variant="outline"
                className="w-full h-12 rounded-xl border-stone-200 hover:bg-stone-50"
                onClick={googleLogin}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <Button 
                type="button"
                variant="ghost"
                className="w-full mt-4 text-stone-500"
                onClick={() => setIsAuthMode(false)}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-violet-400" />
              <span className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>LearnHub</span>
            </div>
            <p className="text-stone-400 text-sm">© 2024 LearnHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
